import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import AdminDeleteModal from "../components/AdminDeleteModal";
import { useAuth } from "../contexts/AuthContext";
import { Plus, KeyRound, Trash2 } from "lucide-react";

function UserForm({ initial, onSubmit, onCancel, mode }) {
    const [form, setForm] = useState({
        username: initial?.username || "",
        full_name: initial?.full_name || "",
        password: "",
        role: initial?.role || "service",
        active: initial?.active ?? true,
    });
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            if (mode === "create") {
                await onSubmit({
                    username: form.username.trim(),
                    full_name: form.full_name,
                    password: form.password,
                    role: form.role,
                });
            } else {
                const patch = {
                    full_name: form.full_name,
                    role: form.role,
                    active: form.active,
                };
                if (form.password) patch.new_password = form.password;
                await onSubmit(patch);
            }
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="space-y-3"
            data-testid="user-form"
        >
            <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Username
                </label>
                <input
                    className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 disabled:bg-gray-50"
                    value={form.username}
                    onChange={(e) => set("username", e.target.value)}
                    disabled={mode === "edit"}
                    required
                    data-testid="user-form-username"
                />
            </div>
            <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Full Name
                </label>
                <input
                    className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    data-testid="user-form-fullname"
                />
            </div>
            <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    {mode === "edit" ? "Reset Password (optional)" : "Temporary Password"}
                </label>
                <input
                    type="password"
                    className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    required={mode === "create"}
                    data-testid="user-form-password"
                />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Role
                    </label>
                    <select
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 bg-white"
                        value={form.role}
                        onChange={(e) => set("role", e.target.value)}
                        data-testid="user-form-role"
                    >
                        <option value="service">Service</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>
                {mode === "edit" && (
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Active
                        </label>
                        <select
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 bg-white"
                            value={form.active ? "yes" : "no"}
                            onChange={(e) => set("active", e.target.value === "yes")}
                            data-testid="user-form-active"
                        >
                            <option value="yes">Active</option>
                            <option value="no">Inactive</option>
                        </select>
                    </div>
                )}
            </div>
            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                    {error}
                </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
                <button
                    type="button"
                    onClick={onCancel}
                    className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                    data-testid="user-form-cancel"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-60"
                    data-testid="user-form-submit"
                >
                    {submitting ? "Saving…" : mode === "create" ? "Create User" : "Save Changes"}
                </button>
            </div>
        </form>
    );
}

export default function Users() {
    const { user: currentUser } = useAuth();
    const isAdmin = currentUser?.role === "admin";
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
            setError("");
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div data-testid="users-page">
            <PageHeader
                title="User Management"
                subtitle="Create, edit, and deactivate ODR Sys accounts"
                actions={
                    <button
                        onClick={() => setShowCreate(true)}
                        className="odr-btn inline-flex items-center gap-2 rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4"
                        data-testid="users-add-button"
                    >
                        <Plus className="w-4 h-4" /> New User
                    </button>
                }
            />

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <table className="odr-table" data-testid="users-table">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Full Name</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>1st Login?</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={6} className="text-center text-gray-500">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && users.length === 0 && (
                            <tr>
                                <td colSpan={6} className="text-center text-gray-500">
                                    No users found.
                                </td>
                            </tr>
                        )}
                        {users.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.username}`}>
                                <td className="odr-mono font-medium">{u.username}</td>
                                <td>{u.full_name || "—"}</td>
                                <td>
                                    <span
                                        className={`odr-badge ${u.role === "admin" ? "odr-badge-past" : "odr-badge-awaiting"}`}
                                    >
                                        {u.role}
                                    </span>
                                </td>
                                <td>
                                    {u.active ? (
                                        <span className="odr-badge odr-badge-open">Active</span>
                                    ) : (
                                        <span className="odr-badge odr-badge-neutral">Inactive</span>
                                    )}
                                </td>
                                <td>
                                    {u.must_change_password ? (
                                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold">
                                            <KeyRound className="w-3.5 h-3.5" /> Pending
                                        </span>
                                    ) : (
                                        <span className="text-gray-400 text-xs">—</span>
                                    )}
                                </td>
                                <td className="text-right">
                                    <button
                                        onClick={() => setEditing(u)}
                                        className="text-sm text-red-700 hover:text-red-900 font-semibold"
                                        data-testid={`edit-user-${u.username}`}
                                    >
                                        Edit
                                    </button>
                                    {isAdmin
                                      && u.username !== "operalta"
                                      && u.id !== currentUser?.id && (
                                        <button
                                            onClick={() => setDeleting(u)}
                                            className="ml-3 text-sm text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                            data-testid={`delete-user-${u.username}`}
                                            title="Hard-delete (admin)"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> Delete
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showCreate && (
                <Modal title="New User" onClose={() => setShowCreate(false)}>
                    <UserForm
                        mode="create"
                        onCancel={() => setShowCreate(false)}
                        onSubmit={async (payload) => {
                            await api.post("/users", payload);
                            setShowCreate(false);
                            await load();
                        }}
                    />
                </Modal>
            )}

            {editing && (
                <Modal title={`Edit ${editing.username}`} onClose={() => setEditing(null)}>
                    <UserForm
                        mode="edit"
                        initial={editing}
                        onCancel={() => setEditing(null)}
                        onSubmit={async (patch) => {
                            await api.patch(`/users/${editing.id}`, patch);
                            setEditing(null);
                            await load();
                        }}
                    />
                </Modal>
            )}

            <AdminDeleteModal
                open={!!deleting}
                module="users"
                recordId={deleting?.id}
                endpoint={deleting ? `/users/${deleting.id}` : ""}
                label={deleting?.username ? `user '${deleting.username}'` : ""}
                onClose={() => setDeleting(null)}
                onDeleted={() => load()}
                testIdPrefix="delete-user"
            />
        </div>
    );
}

function Modal({ title, children, onClose }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={onClose}
            data-testid="modal-overlay"
        >
            <div
                className="w-full max-w-md bg-white border border-gray-200 rounded-sm"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="border-b border-gray-200 px-5 py-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-sm"
                        data-testid="modal-close"
                    >
                        ✕
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}
